// @ts-expect-error React import required to render in Jira SSR
import React from 'react';
import {
	type FrontendStage,
	type PlacementMessageFrontendFactory,
	type AnyProps,
	type NoProps,
	type PlacementRenderer,
	type PlacementBrowserConfig,
} from '@post-office/message-maker-browser/types';

import { createPlacementFrontend } from '@post-office/message-maker-browser/create-placement-frontend';

import { type CustomRenderer } from '@post-office/message-maker-browser/types';

import type { EligibleMessage } from '@post-office/shared-contracts/shared-request-middleware-context/context';

import { usePlacementApi } from '@post-office/use-placement-api';

import { useEffect, useState } from 'react';

export type BaseRenderProps = {
	isInScopeForListMergeOnboarding?: boolean;
	/**
	 * Whether the project has a list preference
	 * Used by jira-nin-list-merge-modal-onboarding-v2 message
	 */
	hasListPreference?: boolean;
	/**
	 * Whether the List is in detailed view
	 * Used by jira-nin-list-merge-modal-onboarding-v2 message
	 */
	isDetailedView?: boolean;
	/**
	 * The target background color
	 * Used by jira-nin-list-merge-spotlight-onboarding-v2 message
	 */
	targetBgColor?: string;
	/**
	 * The project name
	 * Used by jira-nin-paid-list-merge-modal-onboarding message
	 */
	projectName?: string;
	/**
	 * Whether the work item preview panel is currently open
	 * Used by jira-nin-issue-preview-panel-resize message
	 */
	isIssuePreviewPanelOpen?: boolean;
	/**
	 * The view mode the user's stored preference opens work items in, where SIDEBAR is the preview
	 * panel. Known before anything is open, which is what the tour's step count depends on
	 * Used by the jira-nin-issue-preview-panel GA tour
	 */
	preferredViewMode?: 'MODAL' | 'SIDEBAR';
	/**
	 * The view mode a work item is currently open in, observed live. Null when nothing is open.
	 * Non-null at the tour's first render means the intro step is skipped: it anchors to a list row,
	 * so it cannot show once a modal covers the list or a panel has made it redundant
	 * Used by the jira-nin-issue-preview-panel GA tour
	 */
	openViewMode?: 'MODAL' | 'SIDEBAR' | null;
	/**
	 * Opens the work item the intro step is anchored to, in whichever surface the user's preference
	 * dictates. The same call Jira makes for a real row click, so the tour asks the product to
	 * navigate rather than synthesising a click on its own anchor.
	 *
	 * Supplied only from the inline mount, where there is a list row to open at all
	 * Used by the jira-nin-issue-preview-panel GA tour
	 */
	openWorkItem?: () => void;
	/**
	 * Closes whatever work item is open, whichever surface it is in. The same call Jira's own modal
	 * close button and preview panel `onClose` make, so the tour reverses its own navigation rather
	 * than reaching for the product's controls.
	 *
	 * Supplied only from the inline mount, alongside `openWorkItem`
	 * Used by the jira-nin-issue-preview-panel GA tour
	 */
	closeWorkItem?: () => void;
};

export type IssuePreviewPanelSpotlightInlineRenderProps = {
	/**
	 * Callback to set whether or not the "open work item" button should be highlighted
	 */
	setIsActive: (isActive: boolean) => void;
	/**
	 * The current state of the spotlight
	 */
	state: 'active' | 'disabled' | 'inactive' | 'waiting-for-highlight';
	/**
	 *
	 */
	placementType: 'issue-preview-panel-spotlight-inline';
};

export type IssuePreviewPanelSpotlightSidebarRenderProps = {
	placementType: 'issue-preview-panel-spotlight-sidebar';
};

export type PlacementRenderProps =
	| BaseRenderProps
	| (BaseRenderProps &
			(IssuePreviewPanelSpotlightInlineRenderProps | IssuePreviewPanelSpotlightSidebarRenderProps));

type PlacementDefinition = CustomRenderer<PlacementRenderProps>;

const placementConfig: PlacementBrowserConfig = {
	placementId: `issue-navigator-placement`,
};

const placement = {
	createFrontend: createPlacementFrontend<PlacementDefinition['frontend']>(placementConfig),
};

export const createFrontend: <U extends AnyProps = NoProps>(
	placementRenderer: PlacementRenderer<
		{
			render: FrontendStage<PlacementRenderProps>;
		},
		U
	>,
) => PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	U
> = placement.createFrontend;

export const PlacementFrontend: PlacementMessageFrontendFactory<
	{
		render: FrontendStage<PlacementRenderProps>;
	},
	PlacementRenderProps
> = createFrontend<PlacementRenderProps>((Messages) => (appProps) => {
	const { isInScopeForListMergeOnboarding } = appProps;
	const { data, error, isLoading } = usePlacementApi<{
		messages: Array<EligibleMessage<Record<string, never>>>;
	}>(
		'/',
		isInScopeForListMergeOnboarding
			? {
					queryParams: {
						isInScopeForListMergeOnboarding: 'true',
					},
				}
			: undefined,
	);

	const [messages, setMessages] = useState<EligibleMessage<Record<string, never>>[]>([]);
	useEffect(() => {
		if (isLoading || error) {
			return;
		}
		setMessages(data.messages);
	}, [data, error, isLoading]);

	if (error || isLoading || messages.length === 0) {
		return null;
	}

	return (
		!isLoading && (
			<>
				{messages?.map?.((props) => (
					<Messages.render key={props.messageInstanceId} {...props} {...appProps} />
				))}
			</>
		)
	);
});
